/*@jsx jsx*/
import React, { ReactNode } from 'react';
// Couldn't figure out how to make ESLINT happy with JSX
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import paths from 'constants/paths';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { mediaQueries } from 'components/mediaQueries';
import { Link } from 'components/Link/Link';
import { SmallBlueText } from 'components/typography';
import { MiniArticleCard as MiniArticleCardType } from 'data/global/types';
import { Colors } from '@zapier/design-system';
import {
  ArticleImage,
  defaultImageQueryParams,
} from 'components/Images/ArticleImage/ArticleImage';
import { ImageQueryParams } from 'components/Images/RichImageComponent/RichImageComponent';

type Props = { article: MiniArticleCardType };

const miniCardImageParams: Readonly<ImageQueryParams> = {
  ...defaultImageQueryParams,
  w: '448',
};

export const MiniArticleCard = ({ article }: Props) => {
  const trackEvent = useTrackEvent();

  const ArticleLink = ({ children }: { children: ReactNode }) => {
    return (
      <Link
        href={paths.blogArticle(article.slug)}
        data-testId="miniArticleCard-articleLink"
        onClick={(event) =>
          trackEvent('linkClicked', {
            linkText: article.title,
            href: paths.blogArticle(article.slug),
            path: window.location.pathname,
            posYFromTop: event.clientY,
          })
        }
      >
        {children}
      </Link>
    );
  };

  const truncateTitle = (title: string): string => {
    const truncatedTitle = title.slice(0, 45);
    const lastEmptySpace = truncatedTitle.lastIndexOf(' ');

    return truncatedTitle.slice(0, lastEmptySpace) + '...';
  };

  return (
    <CardWrapper data-testid="miniArticleCard">
      <ArticleLink>
        <Image image={article.image} queryParams={miniCardImageParams} />
      </ArticleLink>
      <TextWrapper>
        <Link
          href={article.categoryAssemblyHref}
          color="blue"
          onClick={(event) =>
            trackEvent('linkClicked', {
              linkText: article.categoryAssemblyTitle,
              href: article.categoryAssemblyHref,
              path: window.location.pathname,
              posYFromTop: event.clientY,
            })
          }
        >
          <SmallBlueText
            css={css`
              ${mediaQueries.tablet} {
                margin-bottom: 10px;
              }
            `}
          >
            {article.categoryAssemblyTitle}
          </SmallBlueText>
        </Link>
        <ArticleLink>
          <Title>{article.title}</Title>
          <ShortTitle>{truncateTitle(article.title)}</ShortTitle>
        </ArticleLink>
      </TextWrapper>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  display: grid;
  grid-column-gap: 10px;
  grid-template-columns: min-content 1fr;
  ${mediaQueries.tablet} {
    grid-column-gap: 40px;
    padding: 0;
    &:hover {
      color: ${Colors.blue500};
      img {
        opacity: 50%;
      }
    }
  }
  ${mediaQueries.desktop} {
    grid-template-columns: 1fr;
  }
`;

const Image = styled(ArticleImage)`
  height: 135px;
  width: 135px;
  ${mediaQueries.tablet} {
    height: 130px;
    width: 230px;
  }
  ${mediaQueries.desktop} {
    margin-bottom: 15px;
    height: 126px;
    width: 224px;
  }
`;

const TextWrapper = styled.div`
  align-self: center;
`;

const Title = styled.p`
  display: none;
  font-weight: 600;
  font-size: 16px;
  line-height: 150%;
  ${mediaQueries.tablet} {
    display: grid;
    font-size: 20px;
  }
  ${mediaQueries.desktop} {
    font-size: 16px;
  }
`;

const ShortTitle = styled(Title)`
  display: block;
  ${mediaQueries.tablet} {
    display: none;
  }
`;
